import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { db } from '../firebaseConfig';
import Spinner from '../components/common/Spinner/Spinner';
import { EIXOS } from '../constants/eixos';
import BackButton from '../components/common/BackButton/BackButton';
import { SearchableSelectBase } from '../components/common/SearchableSelect/SearchableSelect';
import PriorizacaoDashboard from '../components/views/PriorizacaoDashboard';
import { calculateIAP } from '../utils/iap';
import { calcularMediaGeral } from '../utils/diagnosticoStats';
import './DiagnosticoPublico.css';

const logo = '/logo_gif.gif';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartDataLabels,
  Filler
);

// ? NOVA FUNÇÃO HELPER PARA ABREVIAR RÓTULOS LONGOS
const abbreviateLabel = (label) => {
  if (label === 'Gestão da Tecnologia da Informação e da Segurança da Informação') {
    return 'Gestão de TI e SI';
  }
  return label;
};

const TABS = [
  { key: 'visaoGeral', label: 'Geral' },
  { key: 'analiseEixos', label: 'Temas' },
  { key: 'evolucao', label: 'Evolução' },
  { key: 'tcu', label: 'Indicadores TCU' },
  { key: 'priorizacao', label: 'Priorização' },
];

// Rótulos canônicos e helpers para evitar problemas de acentuação/encoding
const FASE_LABELS = {
  APRIMORADO: 'Aprimorado',
  INTERMEDIARIO: 'Intermediário',
  INICIAL: 'Inicial',
  INEXPRESSIVO: 'Inexpressivo',
};

const normalizeFase = (label) => {
  const s = String(label || '').normalize('NFC').toLowerCase();
  if (s.includes('aprimo')) return FASE_LABELS.APRIMORADO;
  if (s.includes('intermedi')) return FASE_LABELS.INTERMEDIARIO;
  if (s.includes('inicia')) return FASE_LABELS.INICIAL;
  return FASE_LABELS.INEXPRESSIVO;
};

const getFaseDeAdocaoInfo = (percentual) => {
  if (percentual >= 70) return { texto: FASE_LABELS.APRIMORADO, classe: 'aprimorado' };
  if (percentual >= 40) return { texto: FASE_LABELS.INTERMEDIARIO, classe: 'intermediario' };
  if (percentual >= 15) return { texto: FASE_LABELS.INICIAL, classe: 'inicial' };
  return { texto: FASE_LABELS.INEXPRESSIVO, classe: 'inexpressivo' };
};

// Componente para renderizar o bloco de um eixo (usado tanto no interativo quanto no relatório completo)
const EixoAnalysisBlock = ({
  eixoValue,
  allQuestoes,
  allRespostas,
  isForReport = false,
  selectedEixoAnalise,
  setSelectedEixoAnalise
}) => {
  // Busca informações do eixo
  const eixoInfo = EIXOS.find(e => e.value === eixoValue);
  if (!eixoInfo) return null;

  // Filtra questões e respostas específicas para este bloco de eixo
  const questoesDoEixo = allQuestoes.filter(q => q.eixo === eixoInfo.label);
  const respostasDoEixo = allRespostas.filter(r => questoesDoEixo.some(q => q.id === r.idQuestao));
  const respostasMap = new Map(respostasDoEixo.map(r => [r.idQuestao, r]));

  // Calcula estatísticas
  let respondidas = 0;
  questoesDoEixo.forEach(q => {
    const resp = respostasMap.get(q.id);
    if (resp && resp.resposta_principal) {
      respondidas++;
    }
  });
  const totalQuestoes = questoesDoEixo.length;
  const media = calcularMediaGeral(questoesDoEixo, respostasDoEixo);

  // Prepara dados do gráfico de maturidade para este eixo
  const dist = { [FASE_LABELS.INEXPRESSIVO]: 0, [FASE_LABELS.INICIAL]: 0, [FASE_LABELS.INTERMEDIARIO]: 0, [FASE_LABELS.APRIMORADO]: 0 };
  questoesDoEixo.forEach(q => {
    const resp = respostasMap.get(q.id);
    if (resp && resp.resposta_principal) {
      const pont = resp.pontuacao || 0;
      const faseLabel = getFaseDeAdocaoInfo(pont * 100).texto;
      if (dist[faseLabel] !== undefined) {
        dist[faseLabel]++;
      }
    }
  });

  const totalRespostas = Object.values(dist).reduce((sum, val) => sum + val, 0);
  const maturityLevels = [
    { label: FASE_LABELS.APRIMORADO, color: '#28a745' },
    { label: FASE_LABELS.INTERMEDIARIO, color: '#ffc107' },
    { label: FASE_LABELS.INICIAL, color: '#fd7e14' },
    { label: FASE_LABELS.INEXPRESSIVO, color: '#dc3545' },
  ];

  const chartData = {
    labels: [eixoInfo.label],
    datasets: maturityLevels.map(level => ({
      label: level.label,
      data: totalRespostas > 0 ? [(dist[level.label] / totalRespostas) * 100] : [0],
      backgroundColor: level.color,
    })),
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    animation: isForReport ? false : { duration: 800 }, // Desativa totalmente no relatório
    scales: {
      x: { stacked: true, display: false, max: 100 },
      y: { stacked: true, display: false },
    },
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: { enabled: false },
      datalabels: {
        display: true,
        color: '#fff',
        formatter: (value) => {
          if (value < 5) return '';
          return value.toFixed(0) + '%';
        },
        font: { weight: 'bold', size: 11 },
      },
    },
  };

  return (
    <div className={`eixo-analysis-report-block ${isForReport ? 'report-page-break' : ''}`}>
      {isForReport && <h3 className="print-eixo-title">Tema: {eixoInfo.label}</h3>}

      <div className="analise-eixos-top-row">
        {!isForReport && (
          <div className="chart-card">
            <h3>Selecionar Tema</h3>
            <div className="no-print">
              <SearchableSelectBase
                options={EIXOS}
                value={selectedEixoAnalise || ''}
                onChange={(val) => setSelectedEixoAnalise(val)}
                placeholder="Selecione ou pesquise um tema..."
                getOptionLabel={(e) => e.label || e}
                sx={{ width: '100%' }}
              />
            </div>
          </div>
        )}
        <div className={isForReport ? 'wide-resumo' : ''} style={isForReport ? { width: '100%' } : {}}>
          {!isForReport && <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', marginTop: 0 }}>Resumo do Tema</h3>}
          <div className="kpi-cards-grid">
            <div className="kpi-card">
              <h4>Média do Tema</h4>
              <p className={`kpi-value ${getFaseDeAdocaoInfo(media).classe}`}>
                {media.toFixed(1)}%
              </p>
            </div>
            <div className="kpi-card">
              <h4>Respondidas</h4>
              <p className="kpi-value">{respondidas} / {totalQuestoes}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-card">
        <h3>Distribuição de Maturidade</h3>
        <div className="stacked-bar-chart-container">
          {totalRespostas > 0 ? (
            <Bar key={`chart-${eixoValue}-${isForReport}`} data={chartData} options={chartOptions} />
          ) : (
            <div className="chart-no-data">
              <i className="fas fa-chart-bar"></i>
              <p>Aguardando respostas para gerar a distribuição de maturidade deste tema.</p>
            </div>
          )}
        </div>
      </div>

      <div className="chart-card">
        <h3>Cobertura do Tema</h3>
        <div className="pill-list">
          <span className="pill-item">{respondidas} respondidas</span>
          <span className="pill-item">{Math.max(totalQuestoes - respondidas, 0)} pendentes</span>
        </div>
      </div>
    </div>
  );
};


// PublicLoadingScreen was replaced by the global Spinner component


const DiagnosticoPublico = ({ orgId: orgIdProp }) => {
  const { orgId: orgIdParam } = useParams();
  const orgId = orgIdProp || orgIdParam;
  const allowRegardless = !!orgIdProp;
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [stats, setStats] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [activeTab, setActiveTab] = useState('visaoGeral');
  const [timeRange, setTimeRange] = useState('all'); // Estado para o filtro de tempo
  const [selectedEixoAnalise, setSelectedEixoAnalise] = useState(null);
  const [allQuestoes, setAllQuestoes] = useState([]);
  const [allRespostas, setAllRespostas] = useState([]);
  const [tcuLoading, setTcuLoading] = useState(false);
  const [tcuData, setTcuData] = useState(null);
  const [tcuError, setTcuError] = useState(null);
  const [selectedEixoEvolucao, setSelectedEixoEvolucao] = useState('geral');
  const [priorizacoes, setPriorizacoes] = useState({});
  const [isPrinting, setIsPrinting] = useState(false);

  const CSV_PATH = '/Resultado_Consolidado_iESGo_limpo.csv';

  // ? Função para acionar a impressão da página (exportar para PDF como Relatório)
  const handleExport = () => {
    setIsPrinting(true);
    // Tempo para o React renderizar todas as abas no DOM antes do print
    // Aumentado para 1.5s para garantir renderização de todos os gráficos em máquinas mais lentas
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 1500);
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"' && (i === 0 || line[i - 1] !== '\\')) {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
  };

  const formatPercentage = (value) => {
    const number = Number(value);
    if (isNaN(number)) return '-';
    return number.toFixed(1).replace('.', ',') + '%';
  };

  const evolucaoRef = useRef(null);

  const handleEixoClick = (eixoLabel) => {
    const eixoInfo = EIXOS.find(e => e.label === eixoLabel);
    if (eixoInfo) {
      setSelectedEixoEvolucao(eixoInfo.value);
      setActiveTab('evolucao');
      // Atraso para garantir que a aba foi renderizada antes de rolar
      setTimeout(() => {
        evolucaoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  };

  useEffect(() => {
    document.body.classList.add('diagnostico-publico');
    return () => {
      document.body.classList.remove('diagnostico-publico');
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const orgRef = doc(db, 'organizacoes', orgId);
        const orgSnap = await getDoc(orgRef);
        if (!orgSnap.exists()) {
          setLoading(false);
          return;
        }
        const data = orgSnap.data();
        setOrgData(data);

        if (data.diagnosticoPublico || allowRegardless) {
          setAllowed(true);
          const historicoQuery = query(
            collection(db, 'historico_pontuacoes'),
            where('idOrganizacao', '==', orgId),
            orderBy('dataSnapshot', 'asc')
          );
          const histSnap = await getDocs(historicoQuery);
          const docs = histSnap.docs.map((d) => ({
            ...d.data(),
            dataSnapshot: d.data().dataSnapshot.toDate ? d.data().dataSnapshot.toDate() : d.data().dataSnapshot
          }));
          setHistorico(docs);

          const questoesSnap = await getDocs(collection(db, 'questoes'));
          const respostasSnap = await getDocs(
            query(collection(db, 'respostas'), where('idOrganizacao', '==', orgId))
          );

          const priorizacoesSnap = await getDocs(collection(db, 'organizacoes', orgId, 'priorizacoes'));
          const mapaPriorizacoes = {};
          priorizacoesSnap.forEach(doc => {
            mapaPriorizacoes[doc.id] = doc.data();
          });
          setPriorizacoes(mapaPriorizacoes);

          const todasQuestoes = questoesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          const respostasBrutas = respostasSnap.docs.map(d => d.data());
          const respostasUnicasMap = new Map();
          for (const resposta of respostasBrutas) {
            if (resposta.idQuestao) {
              const entradaExistente = respostasUnicasMap.get(resposta.idQuestao);
              if (!entradaExistente || resposta.resposta_principal) {
                respostasUnicasMap.set(resposta.idQuestao, resposta);
              }
            }
          }
          const respostasLimpa = Array.from(respostasUnicasMap.values());

          setAllQuestoes(todasQuestoes);
          setAllRespostas(respostasLimpa);

          if (todasQuestoes.length > 0) {
            const respostasMap = new Map(respostasLimpa.map(r => [r.idQuestao, r]));
            const distribuicaoMaturidade = {
              [FASE_LABELS.INEXPRESSIVO]: 0,
              [FASE_LABELS.INICIAL]: 0,
              [FASE_LABELS.INTERMEDIARIO]: 0,
              [FASE_LABELS.APRIMORADO]: 0,
            };
            let respondidas = 0;

            // Initialize pontuacoesPorEixo with all EIXOS to ensure all are displayed
            const pontuacoesPorEixo = {};
            EIXOS.forEach(eixo => {
              pontuacoesPorEixo[eixo.label] = { soma: 0, contagem: 0, respondidas: 0 };
            });

            todasQuestoes.forEach(q => {
              if (pontuacoesPorEixo[q.eixo]) { // Ensure the eixo exists in EIXOS
                pontuacoesPorEixo[q.eixo].contagem++;
                const resp = respostasMap.get(q.id);
                if (resp && resp.resposta_principal) {
                  const pont = resp.pontuacao || 0;
                  respondidas++;
                  pontuacoesPorEixo[q.eixo].soma += pont;
                  pontuacoesPorEixo[q.eixo].respondidas++;
                  const fase = normalizeFase(getFaseDeAdocaoInfo(pont * 100).texto);
                  distribuicaoMaturidade[fase]++;
                }
              }
            });

            const totalQuestoes = todasQuestoes.length;
            const taxaParticipacao = totalQuestoes > 0 ? (respondidas / totalQuestoes) * 100 : 0;
            const mediaGeral = calcularMediaGeral(todasQuestoes, respostasLimpa);
            // const riscosNaoTratados = respostasLimpa.filter(r => r.tratamento_risco === 'Não tratado').length;

            const mediasPorEixo = Object.keys(pontuacoesPorEixo).map(eixo => {
              const d = pontuacoesPorEixo[eixo];
              return {
                eixo,
                media: d.contagem > 0 ? (d.soma / d.contagem) * 100 : 0,
              };
            });

            setStats({ mediaGeral, taxaParticipacao, distribuicaoMaturidade, mediasPorEixo, totalQuestoes, respondidas });
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
        if (EIXOS.length > 0 && !selectedEixoAnalise) {
          setSelectedEixoAnalise(EIXOS[0].value);
        }
      }
    };

    fetchData();
  // selectedEixoAnalise e ajustado dentro do efeito; listar causaria laco.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, allowRegardless]);

  // Carrega indicadores do TCU se houver associação na organização
  useEffect(() => {
    const loadTcu = async () => {
      if (!orgData || !orgData.tcuOrganizacao) {
        setTcuData(null);
        setTcuError(null);
        return;
      }
      setTcuLoading(true);
      setTcuError(null);
      try {
        const resp = await fetch(CSV_PATH);
        if (!resp.ok) throw new Error('Falha ao carregar CSV');
        const text = await resp.text();
        const lines = text.split('\n').filter(l => l.trim() !== '');
        if (lines.length < 2) throw new Error('CSV vazio');
        const header = parseCsvLine(lines[0]);
        const rows = lines.slice(1).map(parseCsvLine);
        const orgColIndex = header.findIndex(h => h.toLowerCase().includes('organiza'));
        const matchRow = rows.find(r => (r[orgColIndex] || '').trim() === orgData.tcuOrganizacao);
        if (!matchRow) {
          setTcuData(null);
          setTcuError('Não encontrado no arquivo de indicadores.');
        } else {
          const obj = {};
          header.forEach((key, idx) => {
            obj[key] = matchRow[idx];
          });
          // Remove colunas não relevantes
          // Tenta remover 'ID Base' pela posição 0 (quando aplicável)
          delete obj[header[0]];
          // Remove a coluna de organização
          const orgKey = header[orgColIndex];
          delete obj[orgKey];
          setTcuData(obj);
        }
      } catch (e) {
        setTcuData(null);
        setTcuError('Erro ao carregar indicadores do TCU.');
      } finally {
        setTcuLoading(false);
      }
    };
    loadTcu();
  }, [orgData]);

  const geralMaturityStackedBarData = useMemo(() => {
    if (!stats || !stats.distribuicaoMaturidade) return { labels: [], datasets: [] };

    const distribuicao = stats.distribuicaoMaturidade;
    const total = Object.values(distribuicao).reduce((sum, val) => sum + val, 0);
    if (total === 0) return { labels: ['Maturidade Geral'], datasets: [] };

    const maturityLevels = [
      { label: FASE_LABELS.APRIMORADO, color: '#28a745' },
      { label: FASE_LABELS.INTERMEDIARIO, color: '#ffc107' },
      { label: FASE_LABELS.INICIAL, color: '#fd7e14' },
      { label: FASE_LABELS.INEXPRESSIVO, color: '#dc3545' },
    ];

    return {
      labels: ['Maturidade Geral'],
      datasets: maturityLevels.map(level => ({
        label: level.label,
        data: [(distribuicao[level.label] / total) * 100],
        backgroundColor: level.color,
      })),
    };
  }, [stats]);

  const eixosBarData = useMemo(() => {
    const backgroundColors = stats ? stats.mediasPorEixo.map(e => {
      const maturityInfo = getFaseDeAdocaoInfo(e.media);
      switch (maturityInfo.texto) {
        case FASE_LABELS.APRIMORADO: return '#28a745';
        case FASE_LABELS.INTERMEDIARIO: return '#ffc107';
        case FASE_LABELS.INICIAL: return '#fd7e14';
        case FASE_LABELS.INEXPRESSIVO: return '#dc3545';
        default: return '#cccccc'; // Default color
      }
    }) : [];

    return {
      labels: stats ? stats.mediasPorEixo.map(e => e.eixo) : [],
      datasets: [
        {
          label: 'Desempenho',
          data: stats ? stats.mediasPorEixo.map(e => e.media) : [],
          backgroundColor: backgroundColors,
        },
      ],
    };
  }, [stats]);

  const historicoProcessado = useMemo(() => (
    historico
      .map(h => {
        const d = h.dataSnapshot instanceof Date ? h.dataSnapshot : new Date(h.dataSnapshot);
        // Mantém todos os dados originais do snapshot, apenas garante que a data seja um objeto Date
        return { ...h, date: d };
      })
      .sort((a, b) => a.date - b.date) // A query do Firestore já ordena, mas garantimos a ordem aqui.
  ), [historico]);

  // Filtra o histórico processado com base no período de tempo selecionado
  const historicoFiltrado = useMemo(() => {
    if (timeRange === 'all') {
      return historicoProcessado;
    }

    const now = new Date();
    const startDate = new Date();

    switch (timeRange) {
      case '1y':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case '6m':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case '3m':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case '1m':
        startDate.setMonth(now.getMonth() - 1);
        break;
      default:
        return historicoProcessado;
    }

    return historicoProcessado.filter(h => h.date >= startDate);
  }, [historicoProcessado, timeRange]);

  // ? NOVO: Processa e filtra os dados para o gráfico de evolução, removendo pontos redundantes.
  const dadosGraficoEvolucao = useMemo(() => {
    // 1. Mapeia o histórico para extrair a data e a média do eixo selecionado.
    const dadosComMedia = historicoFiltrado.map(h => {
      const eixoLabel = EIXOS.find(e => e.value === selectedEixoEvolucao)?.label;
      const mediaBruta = selectedEixoEvolucao === 'geral'
        ? h.mediaGeral
        : (h.mediasPorEixo && h.mediasPorEixo[eixoLabel]) || 0;
      const percentual = (Number(mediaBruta) || 0) * 100;
      return {
        date: h.date,
        media: Math.max(0, Math.min(100, percentual)),
      };
    });

    if (dadosComMedia.length < 3) {
      return dadosComMedia; // Não há pontos suficientes para otimizar.
    }

    // 2. Filtra os pontos, mantendo apenas o início e o fim de sequências com valores iguais.
    return dadosComMedia.filter((ponto, index, arr) => {
      // Mantém sempre o primeiro e o último ponto.
      if (index === 0 || index === arr.length - 1) return true;

      const mediaAnterior = arr[index - 1].media;
      const mediaAtual = ponto.media;
      const mediaSeguinte = arr[index + 1].media;

      // Mantém o ponto se o valor dele for diferente do anterior ou do próximo.
      // Isso preserva os pontos de "virada" no gráfico.
      return mediaAtual !== mediaAnterior || mediaAtual !== mediaSeguinte;
    });
  }, [historicoFiltrado, selectedEixoEvolucao]);

  // ? NOVO: Calcula os limites (mínimo e máximo) do eixo Y de forma dinâmica.
  const yAxisBounds = useMemo(() => {
    if (!dadosGraficoEvolucao || dadosGraficoEvolucao.length < 2) {
      return { min: 0, max: 100 }; // Padrão se não houver dados suficientes
    }

    const mediaValues = dadosGraficoEvolucao.map(h => h.media);
    const dataMax = Math.max(...mediaValues);
    const dataMin = Math.min(...mediaValues);

    // Se todos os valores forem idênticos, cria um buffer em torno do valor.
    if (dataMax === dataMin) {
      return { min: Math.max(0, dataMin - 10), max: Math.min(100, dataMax + 10) };
    }

    // Adiciona um buffer de 10 unidades, mas mantém dentro do limite de 0-100.
    const scaleMax = Math.min(100, dataMax + 10);
    const scaleMin = Math.max(0, dataMin - 10);

    // Arredonda para o inteiro mais próximo para uma escala mais limpa.
    return { min: Math.floor(scaleMin), max: Math.ceil(scaleMax) };
  }, [dadosGraficoEvolucao]);

  const lineData = useMemo(() => ({
    labels: dadosGraficoEvolucao.map(h => h.date.toLocaleDateString('pt-BR')),
    datasets: [
      {
        label: selectedEixoEvolucao === 'geral' ? 'Média Geral' : EIXOS.find(e => e.value === selectedEixoEvolucao)?.label || 'Tema',
        data: dadosGraficoEvolucao.map(h => h.media),
        borderColor: '#00A8E8',
        backgroundColor: 'rgba(0,168,232,0.1)',
        fill: true,
        tension: 0.35,
      },
    ],
  }), [dadosGraficoEvolucao, selectedEixoEvolucao]);

  // ? NOVO: Centraliza as opções do gráfico para clareza e adiciona a escala adaptável.
  const lineOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: !isPrinting,
    scales: {
      y: {
        min: yAxisBounds.min,
        max: yAxisBounds.max,
        ticks: {
          // Garante que a formatação use vírgula e uma casa decimal.
          callback: (value) => {
            if (typeof value !== 'number') return value;
            return value.toFixed(1).replace('.', ',') + '%';
          },
        },
      },
    },
    plugins: {
      filler: { propagate: true },
      tooltip: {
        callbacks: {
          // Formata também o valor que aparece ao passar o mouse.
          label: (context) => {
            const eixoInfo = EIXOS.find(e => e.value === selectedEixoEvolucao);
            const label = selectedEixoEvolucao === 'geral' ? 'Média Geral' : (eixoInfo ? eixoInfo.label : 'Tema');
            return `${label}: ${context.parsed.y.toFixed(1).replace('.', ',')}%`;
          },
        },
      },
      // ? NOVO: Adiciona e configura o plugin de datalabels para formatar os valores nos pontos.
      datalabels: {
        formatter: (value) => {
          // Formata o número para ter uma casa decimal, usando vírgula e o símbolo de percentual.
          if (typeof value !== 'number') return '';
          return value.toFixed(1).replace('.', ',') + '%';
        },
        color: '#334155', // Cor escura para o texto, para bom contraste
        anchor: 'end',     // Posição da âncora do label (no ponto)
        align: 'end',      // Alinhamento do label (acima e à direita do ponto)
        font: {
          weight: '600',
        },
      },
    },
  }), [yAxisBounds, selectedEixoEvolucao, isPrinting]); // A opção agora depende dos limites e do eixo selecionado.

  // Removemos useMemos redundantes que agora estão dentro do EixoAnalysisBlock
  // para evitar sobrecarga de processamento.

  // redundante com EixoAnalysisBlock

  const respostasDict = useMemo(() => {
    const dict = {};
    allRespostas.forEach(r => dict[r.idQuestao] = r);
    return dict;
  }, [allRespostas]);

  if (loading) return <Spinner fullScreen />;


  if (!allowed) {
    return (
      <div className="diagnostico-container">
        <BackButton label="Voltar" />
        <h1>{orgData?.nome || 'Organização'}</h1>
        <p>Esta organização não disponibiliza seu diagnóstico publicamente.</p>
      </div>
    );
  }

  return (
    <div className={`diagnostico-container ${isPrinting ? 'is-printing' : ''}`}>
      <div className="no-print">
        <BackButton label="Voltar para Início" />
      </div>

      {/* Cabeçalho exclusivo para o Relatório Impresso */}
      <div className="print-only report-print-header">
        <div className="report-header-left">
          <img src={logo} alt="Logo iESGo" className="report-logo" />
          <div className="report-header-text">
            <div className="report-title">Relatório de Diagnóstico iESGo</div>
            <div className="report-org-name">{orgData?.nome}</div>
          </div>
        </div>
        <div className="report-date">
          <strong>Emissão:</strong> {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
        </div>
      </div>

      <header className="diagnostico-header">
        <div className="header-top">
          <div className="header-title">
            <h1>Diagnóstico Detalhado</h1>

            <p>{orgData?.nome}</p>
          </div>
          <div className="header-actions no-print">
            <button className="action-btn outline" onClick={handleExport}><i className="fas fa-file-export"></i> Exportar Relatório</button>
            <Link to={`/diagnostico/${orgId}/questoes`} className="action-btn primary">Questionário Completo</Link>
          </div>
        </div>

        {stats && (
          <div className="kpi-cards-grid">
            <div className="kpi-card">
              <h4>Média Geral</h4>
              <p className={`kpi-value ${getFaseDeAdocaoInfo(stats.mediaGeral).classe}`}>
                {stats.mediaGeral.toFixed(1)}%
              </p>
            </div>
            <div className="kpi-card">
              <h4>Participação</h4>
              <p className="kpi-value">{stats.taxaParticipacao.toFixed(0)}%</p>
            </div>
            <div className="kpi-card">
              <h4>Respondidas</h4>
              <p className="kpi-value">{stats.respondidas} / {stats.totalQuestoes}</p>
            </div>

          </div>
        )}
      </header >

      {/* Navegação para Desktop */}
      < nav className="desktop-tabs-nav no-print" >
        {
          TABS.map(tab => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? "tab-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))
        }
      </nav >

      {/* Navegação para Mobile (Dropdown) */}
      < div className="mobile-tabs-nav no-print" >
        <SearchableSelectBase
          options={TABS.map(t => ({ id: t.key, label: t.label }))}
          value={activeTab}
          onChange={setActiveTab}
          placeholder="Selecione a aba"
          sx={{ width: '100%' }}
        />
      </div >
      <main>
        {/* 1. Visão Geral */}
        <div id="visaoGeral" className={`tab-content ${(activeTab !== 'visaoGeral' && !isPrinting) ? 'hidden' : ''}`}>
          {isPrinting && <h2 className="print-only print-section-title">1. Visão Geral</h2>}
          <div className="chart-card chart-card-centered">
            <h3>Distribuição de Maturidade</h3>
            <div className="stacked-bar-chart-container">
              {geralMaturityStackedBarData.datasets.length > 0 ? (
                <Bar key={`geral-${isPrinting}`} data={geralMaturityStackedBarData} options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { stacked: true, display: false },
                    y: { stacked: true, display: false },
                  },
                  animation: !isPrinting,
                  plugins: {
                    legend: { display: true, position: 'top' },
                    tooltip: { enabled: false },
                    datalabels: {
                      color: '#fff',
                      formatter: (value) => {
                        if (value < 5) return '';
                        return value.toFixed(0) + '%';
                      },
                      font: { weight: 'bold' },
                    },
                  },
                }} />
              ) : (
                <div className="chart-no-data">
                  <i className="fas fa-chart-bar"></i>
                  <p>Aguardando respostas para gerar a distribuição de maturidade.</p>
                </div>
              )}
            </div>
          </div>
          <div className="chart-card">
            <h3>Desempenho por Eixo</h3>
            <div className="chart-container">
              {eixosBarData.labels.length > 0 ? (
                <Bar key={`eixos-${isPrinting}`} data={eixosBarData} options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: !isPrinting,
                  scales: {
                    x: {
                      ticks: { callback: (value) => value.toFixed(1) + '%' },
                      grid: { color: 'rgba(0, 0, 0, 0)' },
                    },
                  },
                  onClick: (event, elements) => {
                    if (elements.length > 0) {
                      const elementIndex = elements[0].index;
                      const eixoClicado = eixosBarData.labels[elementIndex];
                      handleEixoClick(eixoClicado);
                    }
                  },
                  onHover: (event, chartElement) => {
                    event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
                  },
                  plugins: { datalabels: { display: false }, legend: { display: false } },
                }} />
              ) : (
                <div className="chart-no-data">
                  <i className="fas fa-chart-line"></i>
                  <p>Aguardando dados de desempenho por eixo.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 2. Análise por Eixo */}
        <div id="analiseEixos" className={`tab-content ${(activeTab !== 'analiseEixos' && !isPrinting) ? 'hidden' : ''}`}>
          {isPrinting && <h2 className="print-only print-section-title">2. Análise Detalhada por Eixo</h2>}

          <div className="no-print">
            {selectedEixoAnalise && (
              <EixoAnalysisBlock
                eixoValue={selectedEixoAnalise}
                allQuestoes={allQuestoes}
                allRespostas={allRespostas}
                isForReport={false}
                selectedEixoAnalise={selectedEixoAnalise}
                setSelectedEixoAnalise={setSelectedEixoAnalise}
              />
            )}
          </div>

          <div className="print-only">
            {EIXOS.map((eixo) => (
              <EixoAnalysisBlock
                key={eixo.value}
                eixoValue={eixo.value}
                allQuestoes={allQuestoes}
                allRespostas={allRespostas}
                isForReport={true}
              />
            ))}
          </div>
        </div>

        {/* 3. Evolução Histórica */}
        <div id="evolucao" ref={evolucaoRef} className={`tab-content ${(activeTab !== 'evolucao' && !isPrinting) ? 'hidden' : ''}`}>
          {isPrinting && <h2 className="print-only print-section-title">3. Evolução Histórica</h2>}
          <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
            <div className="chart-header-with-controls">
              <h3>
                Evolução Histórica - {selectedEixoEvolucao === 'geral' ? 'Média Geral' : abbreviateLabel(EIXOS.find(e => e.value === selectedEixoEvolucao)?.label)}
              </h3>
              <div className="chart-controls-wrapper no-print">
                <SearchableSelectBase
                  options={[
                    { id: 'geral', label: 'Média Geral' },
                    ...(stats?.mediasPorEixo?.map(eixo => ({
                      id: EIXOS.find(e => e.label === eixo.eixo)?.value,
                      label: abbreviateLabel(eixo.eixo)
                    })) || [])
                  ]}
                  value={selectedEixoEvolucao}
                  onChange={setSelectedEixoEvolucao}
                  placeholder="Tema de Evolução"
                  sx={{
                    width: '100%',
                    maxWidth: '320px',
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff' }
                  }}
                />
                <SearchableSelectBase
                  options={[
                    { id: 'all', label: 'Desde o Início' },
                    { id: '1y', label: 'Último Ano' },
                    { id: '6m', label: 'Últimos 6 Meses' },
                    { id: '3m', label: 'Últimos 3 Meses' },
                    { id: '1m', label: 'Último Mês' }
                  ]}
                  value={timeRange}
                  onChange={setTimeRange}
                  placeholder="Período"
                  sx={{
                    width: '100%',
                    maxWidth: '220px',
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff' }
                  }}
                />
              </div>
            </div>
            <div className="chart-container" style={{ height: '400px' }}>
              {lineData.labels.length > 0 ? (
                <Line data={lineData} options={lineOptions} />
              ) : (
                <div className="chart-no-data" style={{ height: '100%' }}>
                  <i className="fas fa-chart-line"></i>
                  <p>Aguardando dados históricos para gerar o gráfico de evolução.</p>
                </div>
              )}
            </div>
            <div className="chart-footer-note no-print">
              <i className="fas fa-info-circle"></i>
              <span>Grafico de snapshots diarios (consolidado as 23:59). Alteracoes no numero de questoes ou respostas levam ate 24h para atualizar o historico.</span>
            </div>
          </div>
        </div>

        {/* 4. Indicadores TCU */}
        <div id="tcu" className={`tab-content ${(activeTab !== 'tcu' && !isPrinting) ? 'hidden' : ''}`}>
          {isPrinting && <h2 className="print-only print-section-title">4. Indicadores do TCU</h2>}
          <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
            <h3>Indicadores do TCU</h3>
            {!orgData?.tcuOrganizacao && (
              <p style={{ margin: 0 }}>Nenhuma organização do TCU associada. Peça ao Administrador Master para associar em 'Organizações'.</p>
            )}
            {orgData?.tcuOrganizacao && (
              <>
                <p style={{ marginTop: 0, color: '#64748b' }}>Organização associada: <strong>{orgData.tcuOrganizacao}</strong></p>
                {tcuLoading && <p>Carregando indicadores...</p>}
                {tcuError && <p style={{ color: '#dc3545' }}>{tcuError}</p>}
                {tcuData && (
                  <div className="tcu-table-container">
                    <table className="tcu-table zebra-striping">
                      <thead>
                        <tr>
                          <th>Indicador</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(tcuData).map((key) => (
                          <tr key={key}>
                            <td>{key}</td>
                            <td>{formatPercentage(Number(tcuData[key]) * 100)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 5. Priorização */}
        <div id="priorizacao" className={`tab-content ${(activeTab !== 'priorizacao' && !isPrinting) ? 'hidden' : ''}`}>
          {isPrinting && <h2 className="print-only print-section-title">5. Priorização & Criticidade</h2>}
          <PriorizacaoDashboard
            questoes={allQuestoes}
            priorizacoes={priorizacoes}
            respostas={respostasDict}
            calculateIAP={calculateIAP}
            customEmptyMessage="Nenhuma priorização foi definida pela organização até o momento."
          />
        </div>
      </main>
    </div >
  );
};

export default DiagnosticoPublico;

